import React, { useState } from "react";
import styled from "styled-components";

/* ── Page Container ── */
const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 60px 20px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 720px;
  background: var(--bg-gradient, linear-gradient(180deg, #333, #111));
  border-radius: 16px;
  padding: 48px 40px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);

  @media (max-width: 600px) {
    padding: 32px 20px;
  }
`;

/* ── Header ── */
const Logo = styled.img`
  width: 120px;
  margin-bottom: 8px;
  opacity: 0.9;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #fff;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 28px;
  line-height: 1.5;
`;

/* ── Download Button ── */
const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 36px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #fff;
  background: linear-gradient(135deg, #e8b10d, #f7c100);
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(247, 193, 0, 0.35);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Version = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  margin: 12px 0 0;
`;

/* ── Footer Links ── */
const LinksRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 28px;
`;

const FooterLink = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s;

  &:hover {
    color: rgba(255, 255, 255, 0.8);
  }
`;

/* ── Modal ── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalBox = styled.div`
  background: #1e1e1e;
  color: #ddd;
  border-radius: 12px;
  max-width: 700px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
`;

const ModalClose = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: transparent;
  border: none;
  color: #999;
  font-size: 22px;
  cursor: pointer;

  &:hover {
    color: #ff5c5c;
  }
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  color: #fff;
  margin: 0 0 20px;
`;

const ModalParagraph = styled.p`
  font-size: 14px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 14px;

  strong {
    color: #fff;
  }
`;

const ModalList = styled.ul`
  padding-left: 20px;
  margin-bottom: 14px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  line-height: 1.7;
`;

const ModalListItem = styled.li`
  margin-bottom: 8px;
`;

/* ── Component ── */
const LauncherDownload: React.FC = () => {
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const handleDownload = () => {
    window.location.href =
      "https://ss-rp.com/static/launcher/SunsetLauncher.rar";
  };

  const handleOverlayClick = (
    e: React.MouseEvent<HTMLDivElement>,
    close: () => void
  ) => {
    if (e.target === e.currentTarget) close();
  };

  return (
    <PageWrapper>
      <Card>
        <Logo src="./watchdog.png" alt="Watchdog Logo" />
        <Title>WATCHDOG ANTI-CHEAT</Title>
        <Subtitle>
          Download our client-side anti-cheat launcher to ensure a fair and
          secure gaming experience.
        </Subtitle>

        <DownloadButton onClick={handleDownload}>
          Download Latest
        </DownloadButton>
        <Version>v3.201.34</Version>

        <LinksRow>
          <FooterLink onClick={() => setShowPrivacyModal(true)}>
            Privacy Policy
          </FooterLink>
          <FooterLink onClick={() => setShowTermsModal(true)}>
            Terms of Use
          </FooterLink>
        </LinksRow>
      </Card>

      {/* ── Privacy Modal ── */}
      {showPrivacyModal && (
        <Overlay onClick={(e) => handleOverlayClick(e, () => setShowPrivacyModal(false))}>
          <ModalBox>
            <ModalClose onClick={() => setShowPrivacyModal(false)}>&times;</ModalClose>
            <ModalTitle>Privacy Policy</ModalTitle>
            <ModalParagraph>
              Welcome to Sunset Network / Sunset Roleplay ("Sunset," "we," "our," or
              "us"). This Privacy Policy explains how we collect, use, disclose, and
              safeguard your information when you visit our website ss-rp.com or use our
              gaming community and client-side anti-cheat services (collectively, the
              "Services"). Please read this policy carefully.
            </ModalParagraph>
            <ModalParagraph><strong>Information We Collect</strong></ModalParagraph>
            <ModalParagraph>
              <strong>In-Game:</strong> Username, IP address, and securely encrypted password.
            </ModalParagraph>
            <ModalParagraph>
              <strong>Forums:</strong> Username, email address, and IP address.
            </ModalParagraph>
            <ModalParagraph>
              <strong>Launcher:</strong> Username, password, IP address, Hardware ID (HWID),
              and other anti-cheat related data such as file/process scanning and
              screenshots within the game environment.
            </ModalParagraph>
            <ModalParagraph><strong>How We Use Your Information</strong></ModalParagraph>
            <ModalList>
              <ModalListItem>To manage and maintain user accounts.</ModalListItem>
              <ModalListItem>To enhance security and integrity within our gaming community.</ModalListItem>
              <ModalListItem>To prevent cheating and ensure fair play.</ModalListItem>
              <ModalListItem>To communicate with you regarding your account and updates.</ModalListItem>
            </ModalList>
            <ModalParagraph><strong>How We Share Your Information</strong></ModalParagraph>
            <ModalList>
              <ModalListItem>We do not sell or rent your personal information to third parties.</ModalListItem>
              <ModalListItem>
                We may share your information with trusted partners who assist us in
                operating our Services, so long as those parties agree to keep this
                information confidential.
              </ModalListItem>
              <ModalListItem>
                We may release your information when appropriate to comply with the law,
                enforce our policies, or protect rights, property, or safety.
              </ModalListItem>
            </ModalList>
            <ModalParagraph><strong>Security</strong></ModalParagraph>
            <ModalParagraph>
              We use administrative, technical, and physical security measures to help
              protect your personal information. No security measures are perfect or
              impenetrable, and no method of data transmission can be guaranteed against
              any interception or misuse.
            </ModalParagraph>
            <ModalParagraph><strong>Changes to This Policy</strong></ModalParagraph>
            <ModalParagraph>
              We may update this Privacy Policy from time to time. You are advised to
              review this page periodically for any changes.
            </ModalParagraph>
            <ModalParagraph><strong>Contact Us</strong></ModalParagraph>
            <ModalParagraph>
              If you have any questions, please contact us at Discord: proxuzz &amp; duplon.
            </ModalParagraph>
          </ModalBox>
        </Overlay>
      )}

      {/* ── Terms Modal ── */}
      {showTermsModal && (
        <Overlay onClick={(e) => handleOverlayClick(e, () => setShowTermsModal(false))}>
          <ModalBox>
            <ModalClose onClick={() => setShowTermsModal(false)}>&times;</ModalClose>
            <ModalTitle>Terms of Use</ModalTitle>
            <ModalParagraph>
              By accessing or using our website (ss-rp.com), gameserver for GTA San
              Andreas Multiplayer (SA-MP), and our client-side anti-cheat launcher
              (collectively, the "Services"), you agree to comply with and be bound by
              these Terms. If you do not agree, please do not use our Services.
            </ModalParagraph>

            <ModalParagraph><strong>1. Acceptance of Terms</strong></ModalParagraph>
            <ModalParagraph>
              By using our Services, you agree to be bound by these Terms, our Privacy
              Policy, and any additional terms that may apply.
            </ModalParagraph>

            <ModalParagraph><strong>2. Eligibility</strong></ModalParagraph>
            <ModalParagraph>
              You must be at least 13 years of age to use our Services.
            </ModalParagraph>

            <ModalParagraph><strong>3. Account Registration</strong></ModalParagraph>
            <ModalList>
              <ModalListItem>Provide accurate and complete information during registration.</ModalListItem>
              <ModalListItem>Maintain the security of your password.</ModalListItem>
              <ModalListItem>Notify us immediately of any unauthorized use of your account.</ModalListItem>
            </ModalList>

            <ModalParagraph><strong>4. Use of Services</strong></ModalParagraph>
            <ModalList>
              <ModalListItem>
                Do not use our Services in any manner that could disable, overburden, or
                impair them.
              </ModalListItem>
              <ModalListItem>
                Do not use any automated device or process to access or copy material.
              </ModalListItem>
              <ModalListItem>
                Do not introduce viruses, trojan horses, or other malicious material.
              </ModalListItem>
              <ModalListItem>
                Do not attempt to gain unauthorized access to any part of the Services.
              </ModalListItem>
            </ModalList>

            <ModalParagraph><strong>5. Anti-Cheat Policy</strong></ModalParagraph>
            <ModalList>
              <ModalListItem>
                Allow the launcher to collect data necessary for anti-cheat purposes.
              </ModalListItem>
              <ModalListItem>
                Refrain from attempting to bypass or interfere with anti-cheat measures.
              </ModalListItem>
              <ModalListItem>
                Any attempt to cheat will result in suspension or termination of your
                account.
              </ModalListItem>
            </ModalList>

            <ModalParagraph><strong>6. Intellectual Property</strong></ModalParagraph>
            <ModalParagraph>
              The Services and their contents are owned by Sunset, its licensors, or
              other providers and are protected by intellectual property laws.
            </ModalParagraph>

            <ModalParagraph><strong>7. User Contributions</strong></ModalParagraph>
            <ModalParagraph>
              You retain ownership of your contributions, but grant Sunset a worldwide,
              non-exclusive, royalty-free license to use them in connection with the
              Services.
            </ModalParagraph>

            <ModalParagraph><strong>8. Termination</strong></ModalParagraph>
            <ModalParagraph>
              We may terminate or suspend your access without prior notice for any
              reason, including breach of these Terms.
            </ModalParagraph>

            <ModalParagraph><strong>9. Limitation of Liability</strong></ModalParagraph>
            <ModalParagraph>
              To the fullest extent permitted by law, Sunset shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages resulting
              from your use of the Services.
            </ModalParagraph>

            <ModalParagraph><strong>10. Governing Law</strong></ModalParagraph>
            <ModalParagraph>
              These Terms shall be governed by the laws of the United Kingdom.
            </ModalParagraph>

            <ModalParagraph><strong>11. Changes to Terms</strong></ModalParagraph>
            <ModalParagraph>
              We reserve the right to modify these Terms at any time. Material changes
              will be communicated with at least 30 days' notice.
            </ModalParagraph>

            <ModalParagraph><strong>12. Contact Us</strong></ModalParagraph>
            <ModalParagraph>
              If you have any questions, please contact us at Discord: proxuzz &amp; duplon.
            </ModalParagraph>
          </ModalBox>
        </Overlay>
      )}
    </PageWrapper>
  );
};

export default LauncherDownload;
